import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ChapterImage, Citation, Spans } from "../api";
import ChapterFigure from "../components/ChapterFigure";
import { PicturePlace } from "../components/PictureViewer";
import { useT } from "../i18n";
import AskButton from "./AskButton";
import AskedMark, { askedLines, askedWords } from "./AskedMark";
import ChatSheet from "./ChatSheet";
import { planByParagraph, TOP } from "./figures";
import { useThreads } from "./conversation";
import { useProgressWriter } from "./progressWriter";
import { readFraction, readToEnd, scrollTopFor } from "./readingProgress";
import { textResume } from "./timeline";
import {
  blockAt,
  blockParagraphs,
  blocksOf,
  headingOf,
  sectionNumberOf,
  type Block,
} from "./blocks";
import SectionRule from "./SectionRule";
import { splitLines } from "./sentences";
import { trimSpace } from "../studio/paragraphs";
import { utf8Length, withSpeakers, type Speakers } from "./speakers";
import { ParagraphGap } from "./paragraphGap";
import { wordAtPoint } from "./pressedWord";
import { usePressGesture } from "./usePressGesture";

/**
 * ReadingText is a chapter with no audio: the author's text, and the same long
 * press that asks a question about it.
 *
 * It exists because asking never needed the audio. The ask endpoint takes the
 * sentence as text and finds it in the revision itself, so the only thing a
 * narrated chapter had that this one did not was a manifest to cut lines out
 * of — and `splitLines` cuts them out of the body instead. Before this, a book
 * that had been written but not yet narrated answered no questions at all,
 * which is the wrong way round: the unfinished book is the one with questions
 * still in it.
 *
 * What it deliberately does not have is a tap to seek. There is nothing to seek
 * to, so a tap on a line opens the chat about it instead.
 */
export default function ReadingText({
  chapterID,
  chapterNumber,
  bookID,
  language,
  body,
  allowAsking,
  images = [],
  trackProgress = false,
  footer,
  speakers,
  emphasis,
}: {
  chapterID: string;
  /** The chapter as the reader's list numbers it, for the chat (ADR-0176). */
  chapterNumber?: number;
  /** The book a reader's conversation belongs to. */
  bookID: string;
  /** The book's language, whose titles end no sentence ("" until it loads). */
  language: string;
  body: string;
  /** Asking needs an account: there is nowhere to bill a visitor. */
  allowAsking: boolean;
  /** The chapter's pictures (ADR-0038), laid out among the paragraphs. */
  images?: ChapterImage[];
  /**
   * Whether this is a reader's place to keep (ADR-0058): the reader page, not
   * the studio's view of the working copy.
   */
  trackProgress?: boolean;
  /** What follows the text, above the room left for the chat button. */
  footer?: ReactNode;
  /**
   * Who says each quote, placed in this body by the server (ADR-0125): the
   * studio's labels, or a reader's faces and a play's heads (ADR-0186).
   */
  speakers?: Speakers | null;
  /** The body's italics as byte spans (ADR-0205), drawn over the lines. */
  emphasis?: Spans;
}) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState<string | null>(null);
  const threads = useThreads(bookID);
  const lines = splitLines(body, language);
  // Where each line starts in the body, in bytes: the lines are the body cut
  // at newlines and sentence ends, so counting them back reproduces it.
  const starts: number[] = [];
  let byte = 0;
  lines.forEach((line, i) => {
    if (i > 0 && line.opensParagraph) byte += 1;
    starts.push(byte);
    byte += utf8Length(line.text);
  });
  const figures = planByParagraph(lines, images);
  // Notes and originals (ADR-0154): a card on a block's first line, nothing on
  // its others, so every line keeps its index and its byte start.
  const paragraphs = body
    .split("\n")
    .map(trimSpace)
    .filter((l) => l !== "");
  const inBlock = blockAt(blocksOf(body));
  // Each line's paragraph (a non-blank line of the body); for a blank line,
  // the paragraph before it.
  const paragraphOf: number[] = [];
  let paragraph = -1;
  lines.forEach((line) => {
    if (!line.blank && line.opensParagraph) paragraph++;
    paragraphOf.push(paragraph);
  });
  const isCardLine = (i: number): boolean => {
    const block = inBlock.get(paragraphOf[i]!);
    return (
      !!block && !lines[i]!.blank && lines[i]!.opensParagraph && paragraphOf[i] === block.first
    );
  };
  // Every other line of a block is hidden, and so is a blank line right after
  // one: the card is a box of its own and needs no blank line under it.
  const hidden = (i: number): boolean => inBlock.has(paragraphOf[i]!) && !isCardLine(i);
  // A paragraph that is only a section number is a divider, a box of its own
  // with its own margins: the blank lines under it and the break opening the
  // next paragraph would only add an empty line below it and none above.
  const ruleOf = (i: number): string | null =>
    lines[i]!.blank || !lines[i]!.opensParagraph
      ? null
      : sectionNumberOf(paragraphs[paragraphOf[i]!] ?? "");
  const afterRule: boolean[] = [];
  lines.forEach((_, i) => {
    afterRule.push(i > 0 && (ruleOf(i - 1) !== null || (lines[i - 1]!.blank && afterRule[i - 1]!)));
  });
  // The break opening a line after a blank one ends that blank line, so it is
  // drawn as the shorter paragraph gap — unless the blank line was not drawn.
  const breakBefore = (i: number): ReactNode => {
    if (i === 0 || afterRule[i]) return "";
    const prev = i - 1;
    return lines[prev]!.blank && !hidden(prev) && !afterRule[prev] ? <ParagraphGap /> : "\n";
  };
  const asked = askedLines(threads.lines, chatContext);

  const navigate = useNavigate();
  const location = useLocation();
  const container = useRef<HTMLElement | null>(null);

  // Whether the reader chose that line, by pressing it or its mark, rather
  // than the chat button choosing it for them: only a chosen line offers
  // "who is …?" (ADR-0045).
  const [chatChosen, setChatChosen] = useState(false);
  // The word pressed on the line, for Meaning (ADR-0141).
  const [chatWord, setChatWord] = useState<string | undefined>();
  const openChat = (sentence: string | null, chosen = true, word?: string) => {
    setChatContext(sentence);
    setChatChosen(chosen);
    setChatWord(word);
    setChatOpen(true);
  };

  /**
   * Going to a cited passage, in the two cases retrieval produces.
   *
   * A citation from another chapter is a navigation carrying the quoted text in
   * router state — the same protocol the player uses, so a citation behaves
   * identically whether the chapter it lands in has audio or not. One inside
   * this chapter is a scroll, since there is no playhead to move.
   */
  const visit = useCallback(
    (citation: Citation) => {
      if (citation.chapter_id !== chapterID) {
        navigate(`/read/${citation.chapter_id}`, {
          state: { citation: citation.text },
        });
        return;
      }
      setChatOpen(false);
      scrollToQuote(container.current, citation.text);
    },
    [chapterID, navigate],
  );

  // Arriving from a citation elsewhere: scroll to it once, then clear the
  // state so a refresh does not drag the reader back to it.
  useEffect(() => {
    const cited = (location.state as { citation?: string } | null)?.citation;
    if (!cited) return;
    navigate(location.pathname, { replace: true, state: null });
    scrollToQuote(container.current, cited);
  }, [location.pathname, location.state, navigate]);

  const [cited] = useState(() => !!(location.state as { citation?: string } | null)?.citation);
  const fraction = useReadingProgress(chapterID, container, trackProgress, cited);

  // A reader's pictures open full screen and can be reported (0122).
  const pictures = useMemo(() => ({ bookID, chapterID }), [bookID, chapterID]);
  return (
    <PicturePlace.Provider value={pictures}>
      {trackProgress && <ReadingBar fraction={fraction} />}

      {/* dir="auto" lives here and *only* here. An element with dir="auto"
          skips any descendant carrying its own dir when it looks for the first
          strong directional character, so repeating it on the lines would
          leave this one with nothing to read and lay a Persian chapter out
          left to right. */}
      <article
        ref={container}
        dir="auto"
        data-testid="chapter-text"
        className={`reading-text whitespace-pre-wrap ${allowAsking ? "select-none" : ""}`}
        style={allowAsking ? { WebkitUserSelect: "none", WebkitTouchCallout: "none" } : undefined}
      >
        {figures.get(TOP)?.map((image) => (
          <ChapterFigure key={image.id} image={image} />
        ))}
        {lines.map((line, i) => {
          const figuresAfter = figures
            .get(i)
            ?.map((image) => <ChapterFigure key={image.id} image={image} />);
          if (hidden(i)) return <span key={i}>{figuresAfter}</span>;
          if (isCardLine(i)) {
            const block = inBlock.get(paragraphOf[i]!)!;
            return (
              <span key={i}>
                <BlockCard block={block} paragraphs={blockParagraphs(block, paragraphs)} />
                {figuresAfter}
              </span>
            );
          }
          if (line.blank && afterRule[i]) return <span key={i}>{figuresAfter}</span>;
          const rule = ruleOf(i);
          if (rule !== null) {
            return (
              <span key={i}>
                <SectionRule number={rule} />
                {figuresAfter}
              </span>
            );
          }
          const heading = line.blank ? null : headingOf(paragraphs[paragraphOf[i]!] ?? "");
          if (heading) {
            // A heading is drawn whole on its first line, without its marks.
            return (
              <span key={i}>
                {line.opensParagraph && (
                  <>
                    {breakBefore(i)}
                    <span
                      role="heading"
                      aria-level={Math.min(heading.level + 1, 6)}
                      data-testid="text-heading"
                      className={`font-bold ${heading.level <= 1 ? "text-[1.2em]" : "text-[1.08em]"}`}
                    >
                      {heading.text}
                    </span>
                  </>
                )}
                {figuresAfter}
              </span>
            );
          }
          return (
            <span key={i} data-line={line.blank ? undefined : true}>
              {line.opensParagraph && breakBefore(i)}
              {line.blank || !allowAsking ? (
                withSpeakers(line.text, starts[i]!, speakers, emphasis)
              ) : (
                <>
                  <AskableLine
                    text={line.text}
                    start={starts[i]!}
                    speakers={speakers}
                    emphasis={emphasis}
                    asked={asked.has(line.text.trim())}
                    onAsk={(word) => openChat(line.text.trim(), true, word)}
                  />
                  {asked.has(line.text.trim()) && (
                    <AskedMark onOpen={() => openChat(line.text.trim())} />
                  )}
                </>
              )}
              {/* The chapter's pictures, after the last line of the paragraph
                they belong to. A <figure> is a block inside this text flow,
                which is what puts it between paragraphs rather than in the
                middle of one. */}
              {figuresAfter}
            </span>
          );
        })}
      </article>

      {footer}

      {allowAsking && (
        <>
          {/* Room for the button, so it never sits on the last line. */}
          <div aria-hidden="true" className="h-20" />
          {/* No line: nothing here is banded, and a line the reader cannot
            see chosen would make a question about the story so far look like
            one about that line (ADR-0176). The server reads where they are
            from their saved place. */}
          <AskButton
            onClick={() => openChat(null, false)}
            className="fixed end-4 bottom-[max(1rem,env(safe-area-inset-bottom))]"
          />
        </>
      )}

      {chatOpen && (
        <ChatSheet
          chapterID={chapterID}
          chapterNumber={chapterNumber}
          bookID={bookID}
          threads={threads}
          line={chatContext ?? ""}
          offerCast={chatChosen}
          word={chatWord}
          onVisit={visit}
          onClose={() => setChatOpen(false)}
        />
      )}
    </PicturePlace.Provider>
  );
}

/**
 * useReadingProgress follows how far through the text the reader has
 * scrolled, and keeps it (ADR-0058): the fraction, position 0, and completion
 * once the end has been on screen.
 *
 * Opening the chapter again scrolls back to the saved fraction first, and
 * nothing is saved until it has: the page opens at its top, and saving that
 * would overwrite the place it is about to return to.
 *
 * Measured on arrival as well as on scroll, and every measure is recorded: the
 * writer keeps only the latest in each four-second window.
 */
function useReadingProgress(
  chapterID: string,
  container: RefObject<HTMLElement | null>,
  enabled: boolean,
  /** Arrived at a cited passage, which is where the reader asked to be. */
  cited: boolean,
): number {
  const { saved, settled, record } = useProgressWriter(chapterID, enabled);
  const [fraction, setFraction] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !settled || ready) return;
    const el = container.current;
    // A chapter read to its end opens at its start: going back to one is
    // rereading. Anywhere short of the end, it opens where it was left.
    const resume = saved ? textResume(saved) : null;
    if (el && !cited && resume !== null) {
      const box = el.getBoundingClientRect();
      window.scrollTo({
        top: scrollTopFor(resume, box, window.innerHeight, window.scrollY),
      });
    }
    setReady(true);
  }, [enabled, settled, ready, saved, cited, container]);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = container.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      const at = readFraction(box, window.innerHeight);
      setFraction(at);
      if (ready) {
        const end = readToEnd(box, window.innerHeight);
        record({ position_ms: 0, fraction: at, completed: end, read_to_end: end });
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    // Measured once on arrival too: a chapter shorter than the screen is read
    // without a single scroll.
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enabled, ready, container, record]);

  return fraction;
}

/**
 * ReadingBar is a hairline across the top of the screen that fills from the
 * interface's starting edge as the chapter is read — the text chapter's
 * stand-in for the player's scrubber, and as quiet as one can be.
 */
function ReadingBar({ fraction }: { fraction: number }) {
  const t = useT();
  const percent = Math.round(fraction * 100);
  return (
    <div
      role="progressbar"
      aria-label={t.reader.readSoFar}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      data-testid="reading-progress"
      className="fixed inset-x-0 top-0 z-30 h-[calc(3px+env(safe-area-inset-top))] bg-ground/60 pt-[env(safe-area-inset-top)]"
    >
      <div
        className="h-full bg-sage transition-[width] duration-150"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

/**
 * BlockCard is a note or an original (ADR-0154), closed to one line under the
 * paragraph before it. A <details>, so a tap, Enter and Space all open it and
 * the browser keeps what is open. The text keeps its lines — a couplet is two
 * — and is not askable: it is somebody's note about the book, not the book.
 */
function BlockCard({ block, paragraphs }: { block: Block; paragraphs: string[][] }) {
  const t = useT();
  const note = block.kind === "note";
  const label = block.label || (note ? t.reader.blockNote : t.reader.blockOriginal);
  const preview = paragraphs[0]?.join(" / ") ?? "";
  return (
    <details
      data-testid="text-block"
      data-kind={block.kind}
      className="group my-1 block rounded-card border border-line bg-sunk/50 text-[0.86em] leading-[1.95] whitespace-normal text-ink-soft"
    >
      <summary className="flex cursor-pointer list-none items-baseline gap-2.5 px-3.5 py-2 [&::-webkit-details-marker]:hidden">
        <span
          className={`shrink-0 rounded-md px-2 py-0.5 text-[0.78em] font-bold ${
            note ? "bg-sage-soft text-sage-deep" : "bg-sky-soft text-sky"
          }`}
        >
          {label}
        </span>
        <span className="min-w-0 flex-1 truncate group-open:hidden">{preview}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="ms-auto size-[0.9em] shrink-0 self-center text-muted transition-transform group-open:rotate-180"
        >
          <path
            d="M3.5 6l4.5 4.5L12.5 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </summary>
      <div className="flex flex-col gap-2 px-3.5 pb-3">
        {paragraphs.map((p, i) => (
          <p key={i} className="whitespace-pre-line">
            {p.join("\n")}
          </p>
        ))}
      </div>
    </details>
  );
}

/**
 * One pressable sentence.
 *
 * A <button> rather than a span with a handler, for the same reason the player
 * uses one: Enter and Space then reach the sheet natively, so the keyboard and
 * the finger take one code path instead of two that drift.
 */
function AskableLine({
  text,
  start,
  speakers,
  emphasis,
  asked,
  onAsk,
}: {
  text: string;
  /** Where the line starts in the body, in bytes, for its faces and heads. */
  start: number;
  speakers?: Speakers | null;
  emphasis?: Spans;
  /** The reader's chat is about this line. */
  asked: boolean;
  /** Opens the chat about this line, with the word long-pressed, if one was. */
  onAsk: (word?: string) => void;
}) {
  // A plain tap opens the sheet too: there is nothing to seek to, and on a
  // phone it is the gesture people actually make. Only a long press names a
  // word, because naming one asks its Meaning at once (ADR-0141, 11-01) and
  // a tap is too easy to make by accident to spend on.
  // Where the finger went down, for the word under it.
  const at = useRef<{ x: number; y: number } | null>(null);
  const press = usePressGesture({
    onTap: () => onAsk(),
    onLongPress: () => onAsk(at.current ? wordAtPoint(at.current.x, at.current.y) : undefined),
  });
  return (
    <button
      type="button"
      {...press}
      onPointerDown={(e) => {
        at.current = { x: e.clientX, y: e.clientY };
        press.onPointerDown(e);
      }}
      data-testid="askable-line"
      data-asked={asked ? "true" : undefined}
      className={`-mx-[0.1em] -my-[0.18em] inline cursor-pointer rounded-[0.25rem] px-[0.1em] py-[0.18em] text-start text-inherit transition-colors hover:bg-sunk ${asked ? askedWords : ""}`}
    >
      {withSpeakers(text, start, speakers, emphasis)}
    </button>
  );
}

/**
 * scrollToQuote brings a cited passage into view and flashes it.
 *
 * Matched on text rather than on the citation's offset: the offset is into the
 * revision body, and what is on screen is that body cut into lines, so the two
 * agree only until the first line boundary. A citation that cannot be found is
 * left alone rather than scrolling somewhere arbitrary.
 */
function scrollToQuote(root: HTMLElement | null, quote: string) {
  if (!root) return;
  const needle = quote.trim().slice(0, 40);
  if (!needle) return;
  const lines = Array.from(root.querySelectorAll<HTMLElement>('[data-testid="askable-line"]'));
  const target =
    lines.find((el) => (el.textContent ?? "").includes(needle)) ??
    lines.find((el) => needle.includes((el.textContent ?? "").trim()));
  if (!target) return;
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  target.dataset.cited = "true";
  setTimeout(() => delete target.dataset.cited, 2500);
}
